import {
  Alert,
  Box,
  Button,
  Flex,
  Image,
  Link,
  ListItem,
  OrderedList,
  Spinner,
  Stack,
  Table,
  TableCellProps,
  Tbody,
  Td,
  Text,
  Tr,
  VStack,
} from '@chakra-ui/react'
import {
  getDigitalSignatureInstructions,
  getDigitalSignatureSecondInstructions,
} from 'BrokerTsLib/src/dtos'
import {
  ContractAcceptationType,
  Insurer,
  Risk,
  SelectionType,
} from 'BrokerTsLib/src/enums'
import { formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import QRCode from 'react-qr-code'

import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { Loading } from '../../components/Loading'
import { useSelections } from '../../hooks'
import { useGetInsurance } from '../../hooks/useInsuranceParams'
import { useGetVehicleInspection } from '../../hooks/useOffer'
import { useConfirmContract, useGetIsContractConfirmed } from '../../hooks/useWizard'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { meetingRecordContext } from '../../pages/carkulka/wizard/5'
import { logos } from '../../utils/companiesLogos'
import { CancelContract } from '../CancelContract'
import { insurerOfferContext } from '../hoc/WithOffer'

interface RecapTableRowProps extends TableCellProps {
  col1: string | React.ReactNode
  col2?: string | React.ReactNode
  col3?: string | React.ReactNode
  isHighlighted?: boolean
}

export const RecapTableRow = ({
  col1,
  col2,
  col3,
  isHighlighted,
  sx,
  ...rest
}: RecapTableRowProps) => {
  return (
    <Tr sx={{ m: 0, p: 0 }}>
      <Td sx={{ w: '15rem', px: 0, py: 1, ...sx }}>
        {col1 && (
          <Text sx={{ fontWeight: isHighlighted ? 'semibold' : 'medium' }}>
            {col1}
          </Text>
        )}
      </Td>
      <Td sx={{ px: 0, py: 1, ...sx }}>{col2 && col2}</Td>
      {col3 && (
        <Td
          sx={{
            px: 0,
            py: 1,
            fontWeight: isHighlighted ? 'semibold' : 'normal',
            textAlign: 'end',
            ...sx,
          }}
        >
          {col3}
        </Td>
      )}
    </Tr>
  )
}

export const Recap = () => {
  const {
    data: dataPf,
    isLoading: isLoadingPf,
    isFetching: isFetchingPf,
  } = useSelections(SelectionType.FrekvencePlatby)
  const {
    data: dataFs,
    isLoading: isLoadingFs,
    isFetching: isFetchingFs,
  } = useSelections(SelectionType.FormaSjednani)
  const pfEnum = dataPf?.data ?? []
  const fsEnum = dataFs?.data ?? []
  const { data: dataDp } = useSelections(SelectionType.ZpusobDigitalnihoPodpisu)
  const dpEnum = dataDp?.data ?? []

  const { insuranceParams, refetchInsuranceParams } =
    useInsuranceParamsContext()
  const { isLoading, offer } = useContext(insurerOfferContext)
  const cat = insuranceParams?.generalParams.contractAcceptationType
  const offerId = insuranceParams?.generalParams.selectedOfferId

  const { meetingRecord, isLoading: isMRLoading } =
    useContext(meetingRecordContext)

  const { data: vehicleInspectionData, refetch: refetchVehicleInspection } =
    useGetVehicleInspection(offerId!, { enabled: false })

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const isCanceled = !!insuranceParams?.generalParams.contractCanceled

  const {
    data: dataInsurance,
    refetch: refetchInsurance,
    isLoading: isLoadingInsurance,
  } = useGetInsurance(offerId!)
  const insurance = dataInsurance?.data ?? {}

  const [contractConfirmInfo, setContractConfirmInfo] = useState<string | null>(null)
  const [contractConfirmed, setContractConfirmed] = useState<boolean>(false)

  const { mutateAsync: confirmContractAsync, isLoading: isConfirmingContract } = useConfirmContract()
  const { refetch: fetchIsContractConfirmed } = useGetIsContractConfirmed(insuranceParams?.generalParams.paramsId!, { enabled: false })

  useEffect(() => {
    if (offer) {
      refetchVehicleInspection()
    }
    if (offer && offer.insurerId == Insurer.Csob && cat === ContractAcceptationType.DigitalSignature) {
      fetchIsContractConfirmed().
        then((resp) => {
          if (resp.data?.data?.success) {
            setContractConfirmed(true)
          }
        })
    }
  }, [offer])

  const povPart = offer?.parts.find((x) => x.riskId === Risk.POV)
  const havPart = offer?.parts.find((x) => x.riskId === Risk.HAV)
  let spolPartName = offer?.parts.find((x) => x.riskId === Risk.SPOL)?.name
  if (havPart && havPart.name && spolPartName) {
    if (havPart.name.toLowerCase().indexOf('spoluúčast') >= 0)
      spolPartName = undefined
  }

  let paymentDate = ''
  if (isContracted && cat === ContractAcceptationType.Payment) {
    // CARKULKA-2503 - výchozí hodnota pro splatnost je datum včetně času
    // pokud se shoduje s datem sjednání, ale čas je 0:00, tak čas nezobrazujeme
    paymentDate = moment(new Date(insuranceParams?.generalParams.paymentDate!)).format('D. M. YYYY HH:mm')
    if (
      moment(new Date(insuranceParams?.generalParams.paymentDate!)).format('YYYY-MM-DD') ==
      moment(new Date(insuranceParams?.generalParams.contractCreatedDate!)).format('YYYY-MM-DD') &&
      paymentDate.endsWith('00:00')
    ) {
      paymentDate = moment(new Date(insuranceParams?.generalParams.paymentDate!)).format(
        'D. M. YYYY',
      )
    }
  }

  const dpDescription = dpEnum
    .find((x) => x.id == (meetingRecord?.digitalSignatureType ?? 0))
    ?.description?.toLowerCase()
  let instructions: string[] = []
  let instructions2: string[] = []
  let signatureUrl = insurance?.digitalSignatureUrl
  let signatureSecondUrl = null
  if (offer && meetingRecord?.digitalSignatureType) {
    instructions = getDigitalSignatureInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )
    instructions2 = getDigitalSignatureSecondInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )

    if (
      instructions2.length > 0 &&
      signatureUrl &&
      signatureUrl?.indexOf('|') != -1
    ) {
      const urls = signatureUrl!.split('|')
      signatureUrl = urls[0]
      signatureSecondUrl = urls[1]
    }
  }

  let showPartPrizes = true
  if (
    offer?.insurerId == Insurer.Allianz &&
    offer?.parts.every((x) => x.price == 0)
  ) {
    showPartPrizes = false
  }

  const setConfirmCsob = async () => {
    await confirmContractAsync({ paramsId: insuranceParams!.generalParams.paramsId!.toString(), offerId: offerId! })
      .then((result) => {
        if (result.status === 200 && result.data) {
          if (result.data.success) {
            setContractConfirmed(true)
          }
          else {
            setContractConfirmInfo(result.data.error ?? 'Zjištění stavu podepsání smlouvy selhalo')
          }
        }
      })
      .catch((e) =>
        alert('Zjištění stavu podepsání smlouvy selhalo')
      )
  }

  return (
    <FormSection variant={isCanceled ? 'red' : 'default'}>
      <FormSectionHeader>
        <FormSectionTitle>Rekapitulace pojištění</FormSectionTitle>

        {offer && (
          <FormSectionDescription>
            <Image
              src={logos[offer.insurerId]}
              alt={offer.insurerNameFull}
              width="64px"
              height="48px"
            />
          </FormSectionDescription>
        )}
      </FormSectionHeader>

      {!offer && isLoading && <Loading large={true} />}
      {offer && (
        <FormSectionBody>
          <Table variant="unstyled">
            <Tbody>
              {povPart && (
                <RecapTableRow
                  col1="POV"
                  col2={povPart.name}
                  col3={
                    showPartPrizes ? formatPrice({ price: povPart.price }) : ''
                  }
                />
              )}

              {havPart && (
                <RecapTableRow
                  col1="HAV"
                  col2={`${havPart.name}${spolPartName ? `, ${spolPartName}` : ''}`}
                  col3={
                    showPartPrizes ? formatPrice({ price: havPart.price }) : ''
                  }
                />
              )}

              {offer &&
                offer?.parts
                  .filter(
                    (x) =>
                      (![Risk.POV, Risk.HAV, Risk.SPOL, Risk.GAP].includes(
                        x.riskId,
                      ) &&
                        x.included) ||
                      (x.riskId == Risk.GAP && x.valid && x.included),
                  )
                  .map((part, index) => (
                    <RecapTableRow
                      key={part.riskId}
                      col1={index === 0 ? 'Připojištění' : ''}
                      col2={
                        part.parentId ? (
                          <Text ml="5">{part.name}</Text>
                        ) : (
                          <>{part.name}</>
                        )
                      }
                      col3={
                        showPartPrizes
                          ? formatPrice({
                              price: part.price,
                              zeroLabel: part.parentId ? '' : undefined,
                            })
                          : ''
                      }
                    />
                  ))}

              <RecapTableRow
                isHighlighted
                col1="Celkem"
                col3={formatPrice({ price: offer.price })}
              />
            </Tbody>
          </Table>

          <Table variant="unstyled">
            <Tbody>
              {isContracted && (
                <>
                  <RecapTableRow
                    col1="Způsob sjednání"
                    col2={
                      fsEnum.find(
                        (x) =>
                          x.value ===
                          insuranceParams?.generalParams.contractAcceptationType?.toString(),
                      )?.description
                    }
                  />
                  <RecapTableRow
                    col1="Datum sjednání"
                    col2={moment(
                      new Date(
                        insuranceParams?.generalParams.contractCreatedDate!,
                      ),
                    ).format('D. M. YYYY HH:mm')}
                  />
                </>
              )}
              <RecapTableRow
                col1="Počátek pojištění"
                col2={moment(
                  new Date(insuranceParams?.generalParams.insuranceStartDate!),
                ).format('D. M. YYYY HH:mm')}
              />
              <RecapTableRow
                col1="Splátky pojistného"
                col2={
                  pfEnum.find((x) => x.id === offer.paymentFrequencyId)
                    ?.description
                }
              />
              <RecapTableRow
                col1="Splátka"
                col2={formatPrice({ price: offer.priceFrequency })}
              />
              {isContracted && cat === ContractAcceptationType.Payment && (
                <RecapTableRow
                  col1="Splatnost prvního pojistného"
                  col2={paymentDate}
                />
              )}
              {isCanceled && (
                <RecapTableRow
                  col1="Smlouva byla stornována"
                  col2={`${moment(new Date(insuranceParams.generalParams.contractCanceledDate!)).format('D. M. YYYY HH:mm')}, ${insuranceParams.generalParams.contractCanceledBy}`}
                />
              )}
            </Tbody>
          </Table>

          {!isContracted &&
            offer?.underwriting?.required &&
            offer.underwriting?.message && (
              <Alert colorScheme="orange">
                <Stack direction="column">
                  <Text>{offer.underwriting.message}</Text>
                </Stack>
              </Alert>
            )}

          {isContracted && !isCanceled &&
            (cat === ContractAcceptationType.Payment ||
              cat === ContractAcceptationType.DigitalSignature ||
              (vehicleInspectionData?.data.length ?? 0) > 0 ||
              offer.infos) && (
              <Alert colorScheme="orange">
                <Stack direction="column">
                  {cat === ContractAcceptationType.Payment && (
                    <Text>
                      Pokud nebude první pojistné uhrazeno v termínu, PS
                      nevznikne!
                    </Text>
                  )}

                  {cat === ContractAcceptationType.DigitalSignature && contractConfirmed && (
                    <Text>
                      Smlouva byla digitálně podepsána
                    </Text>
                  )}

                  {cat === ContractAcceptationType.DigitalSignature &&
                    dpDescription && !contractConfirmed && (
                      <Text>
                        Doplňující informace k podpisu smlouvy - podpis{' '}
                        <Text as="span" sx={{ fontWeight: 600 }}>
                          {dpDescription}
                        </Text>
                      </Text>
                    )}

                  {cat === ContractAcceptationType.DigitalSignature &&
                    !!signatureUrl && !contractConfirmed && (
                      <>
                        <Text>
                          Pokud sjednáváte na zařízení s dotykovou obrazovkou
                          (mobil, tablet) můžete přímo přejít do
                          <Link
                            as={'a'}
                            target="_blank"
                            href={signatureUrl}
                            sx={{
                              color: 'primary.500',
                              fontWeight: 'medium',
                              paddingLeft: '5px',
                              textDecoration: 'underline',
                            }}
                          >
                            aplikace pro podepsání{' '}
                            {!!signatureSecondUrl
                              ? 'předsmluvního dokumentu'
                              : 'smlouvy'}
                          </Link>
                          .
                        </Text>
                        <Text sx={{ pb: 1 }}>
                          V opačném případě naskenujte QR kód a pokračujte dle
                          kroků níže.
                        </Text>
                        <Flex gap={4}>
                          <Box sx={{ maxWidth: '128px' }}>
                            <QRCode
                              value={signatureUrl}
                              viewBox={`0 0 256 256`}
                              level="M"
                              size={256}
                              style={{
                                height: 'auto',
                                maxWidth: '100%',
                                width: '100%',
                              }}
                            />
                          </Box>
                          <Box>
                            <OrderedList>
                              {instructions.map((instruction) => (
                                <ListItem key={instruction}>
                                  {instruction}
                                </ListItem>
                              ))}
                            </OrderedList>
                          </Box>
                        </Flex>
                      </>
                    )}

                  {cat === ContractAcceptationType.DigitalSignature &&
                    !!signatureSecondUrl && !contractConfirmed && (
                      <>
                        <Text>
                          Dále na zařízení s dotykovou obrazovkou (mobil,
                          tablet) můžete přejděte do
                          <Link
                            as={'a'}
                            target="_blank"
                            href={signatureSecondUrl}
                            sx={{
                              color: 'primary.500',
                              fontWeight: 'medium',
                              paddingLeft: '5px',
                              textDecoration: 'underline',
                            }}
                          >
                            aplikace pro podepsání smlouvy
                          </Link>
                          .
                        </Text>
                        <Text sx={{ pb: 1 }}>
                          Nebo naskenujte QR kód a pokračujte dle kroků níže.
                        </Text>
                        <Flex gap={4}>
                          <Box sx={{ maxWidth: '128px' }}>
                            <QRCode
                              value={signatureSecondUrl}
                              viewBox={`0 0 256 256`}
                              level="M"
                              size={256}
                              style={{
                                height: 'auto',
                                maxWidth: '100%',
                                width: '100%',
                              }}
                            />
                          </Box>
                          <Box>
                            <OrderedList>
                              {instructions2.map((instruction) => (
                                <ListItem key={instruction}>
                                  {instruction}
                                </ListItem>
                              ))}
                            </OrderedList>
                          </Box>
                        </Flex>
                      </>
                    )}

                  {cat === ContractAcceptationType.DigitalSignature &&
                    !signatureUrl && !contractConfirmed && (
                      <OrderedList>
                        {instructions.map((instruction) => (
                          <ListItem key={instruction}>{instruction}</ListItem>
                        ))}
                      </OrderedList>
                    )}

                  {cat === ContractAcceptationType.DigitalSignature &&
                    offer?.insurerId == Insurer.Csob && !contractConfirmed && (
                      <>
                        <Text sx={{ fontWeight: '600' }}>Nakonec je nutno potvrdit podepsání dokumentů tlačítkem níže. Bez tohoto potvrzení nedojde k aktivaci pojistné smlouvy v pojišťovně.</Text>
                        <Flex sx={{ w: '100%', placeContent: 'start', alignItems: 'baseline' }}>
                          <Button variant='outline' colorScheme='green'
                            onClick={() => setConfirmCsob()}
                            sx={{ mr: 4 }}
                          >Potvrdit podepsání {isConfirmingContract && <Spinner />}</Button>

                          {contractConfirmInfo && <Text>{contractConfirmInfo}</Text>}
                        </Flex>
                      </>
                    )}

                  {offer.infos &&
                    offer.infos
                      .filter(
                        (x) =>
                          x.message &&
                          x.message.indexOf('foto') === -1 &&
                          x.value >= 1000,
                      )
                      .map((x) => (
                        <Text
                          key={x.value}
                          dangerouslySetInnerHTML={{ __html: x.message }}
                        ></Text>
                      ))}
                  {vehicleInspectionData?.data.map(
                    (x, index) =>
                      x.text && (
                        <VStack key={index} alignItems="start">
                          Odkaz na upload fotografií
                          <Text
                            fontWeight="bold"
                            variant="danger"
                            dangerouslySetInnerHTML={{ __html: x.text }}
                          ></Text>
                          {x.uploadUrl && (
                            <Link
                              as="a"
                              variant="underline"
                              target="_blank"
                              href={x.uploadUrl}
                            >
                              Odkaz na upload fotografií
                            </Link>
                          )}
                          {x.description && (
                            <Text variant="danger">{x.description}</Text>
                          )}
                        </VStack>
                      ),
                  )}
                </Stack>
              </Alert>
            )}

          {insuranceParams?.generalParams.insuranceId && (
            <CancelContract
              callback={refetchInsuranceParams}
              isContracted={isContracted}
              contractCanceled={isCanceled}
              enableReactivation={true}
              insuranceId={insuranceParams?.generalParams.insuranceId}
            />
          )}
        </FormSectionBody>
      )}
    </FormSection>
  )
}
